// src/plugins/autotrack/modules/BaseTrackingModule.ts

import { IUniqodeInsightsClient } from '../../../types/sdk';
import { TrackingEvent } from '../types';
import { ILogger } from '../../../utils/logger';

export abstract class BaseTrackingModule {
  protected client?: IUniqodeInsightsClient;
  protected logger?: ILogger;
  protected isActive = false;
  protected eventListeners: Array<{
    element: Element | Document | Window;
    event: string;
    handler: EventListener;
    options?: boolean | AddEventListenerOptions;
  }> = [];

  constructor(protected moduleName: string) {}

  /**
   * Initialize the module with SDK client
   */
  public initialize(client: IUniqodeInsightsClient, logger?: ILogger): void {
    this.client = client;
    this.logger = logger;
    this.logger?.debug(`[${this.moduleName}] ${this.moduleName} initialized`);
    this.isActive = true;
    this.setupTracking();
  }

  /**
   * Start tracking
   */
  public start(): void {
    if (this.isActive) {
      this.logger?.debug(`[${this.moduleName}] ${this.moduleName} already active`);
      return;
    }

    this.isActive = true;
    this.setupTracking();
    this.logger?.debug(`[${this.moduleName}] ${this.moduleName} started`);
  }

  /**
   * Stop tracking
   */
  public stop(): void {
    if (!this.isActive) {
      this.logger?.debug(`[${this.moduleName}] ${this.moduleName} already inactive`);
      return;
    }

    this.isActive = false;
    this.removeAllListeners();
    this.logger?.debug(`[${this.moduleName}] ${this.moduleName} stopped`);
  }

  /**
   * Destroy the module
   */
  public destroy(): void {
    this.stop();
    this.cleanup();
    this.logger?.debug(`[${this.moduleName}] ${this.moduleName} destroyed`);
  }

  /**
   * Track an event through the SDK
   */
  protected trackEvent(event: TrackingEvent): void {
    this.logger?.debug(`[${this.moduleName}] Tracking event:`, event);
    if (!this.client || !this.isActive) {
      return;
    }
    this.logger?.debug(`[${this.moduleName}] Tracking event:`, event);
    this.client.track(event.eventType, event.properties);
    this.logger?.debug(`[${this.moduleName}] Tracked event: ${event.eventType}`, event.properties);
  }

  /**
   * Add event listener and track it for cleanup
   */
  protected addEventListener(
    element: Element | Document | Window,
    event: string,
    handler: EventListener,
    options?: boolean | AddEventListenerOptions
  ): void {
    element.addEventListener(event, handler, options);
    this.eventListeners.push({ element, event, handler, options });
  }

  /**
   * Remove all event listeners
   */
  protected removeAllListeners(): void {
    this.eventListeners.forEach(({ element, event, handler, options }) => {
      element.removeEventListener(event, handler, options);
    });
    this.eventListeners = [];
  }

  /**
   * Abstract method to setup module-specific tracking
   */
  protected abstract setupTracking(): void;

  /**
   * Abstract method for module-specific cleanup
   */
  protected abstract cleanup(): void;

  /**
   * Check if module is currently active
   */
  public isModuleActive(): boolean {
    return this.isActive;
  }

  /**
   * Get module name
   */
  public getModuleName(): string {
    return this.moduleName;
  }
}
